import { useEffect, useState } from "react";
import { DtoTestSet, DtoTestSetItem } from "@/types/dto-types";
import { GenericObject } from "@/types";

export interface UseItemsIteratorProps {
  currentSet: DtoTestSet;
  currentEntries: GenericObject;
  onComplete: () => void;
  onNextItem: (item: DtoTestSetItem) => void;
  onPreviousItem: (item: DtoTestSetItem) => void;
  initialItemId: string | undefined;
}

export function useItemsIterator({
  currentSet,
  currentEntries = {},
  onComplete,
  onNextItem,
  onPreviousItem,
  initialItemId,
}: UseItemsIteratorProps) {
  const [currentItemIndex, setCurrentItemIndex] = useState(0);
  const [currentItemSubIndex, setCurrentItemSubIndex] = useState(0);
  const currentItem = currentSet.items[currentItemIndex] ?? {};
  const isVideoStep = !!(currentItem.video && currentItemSubIndex === 0);
  const isAfterVideoStep = currentItem.video && currentItemSubIndex === 1;
  const canGoToNextItem =
    isVideoStep ||
    !currentItem.mandatory ||
    (currentEntries[currentItem.dbColumn] !== undefined &&
      currentEntries[currentItem.dbColumn] !== null);
  const canGoPreviousItem = isAfterVideoStep || currentItemIndex > 0;

  useEffect(() => {
    if (initialItemId) {
      const initialIndex = currentSet.items.findIndex(
        item => item._id === initialItemId
      );
      if (initialIndex !== -1) {
        setCurrentItemIndex(initialIndex);
      }
    }
  }, [initialItemId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleNextClick = () => {
    if (!canGoToNextItem) {
      return;
    }

    if (isVideoStep) {
      setCurrentItemSubIndex(prevState => prevState + 1);
      return;
    }

    const newIndex = currentItemIndex + 1;
    if (newIndex < currentSet.items.length) {
      setCurrentItemIndex(newIndex);
      const newItem = currentSet.items[newIndex];
      onNextItem(newItem);
    } else {
      onComplete();
      setCurrentItemIndex(0);
    }

    setCurrentItemSubIndex(0);
  };

  const handlePreviousClick = () => {
    if (!canGoPreviousItem) {
      return;
    }

    if (isAfterVideoStep) {
      setCurrentItemSubIndex(prevState => prevState - 1);
      return;
    }

    const newIndex = currentItemIndex - 1;
    const newItem = currentSet.items[newIndex];

    if (newItem && newItem.video) {
      setCurrentItemSubIndex(1);
    }

    setCurrentItemIndex(newIndex);
    onPreviousItem(newItem);
  };

  return {
    currentItemIndex,
    canGoToNextItem,
    canGoPreviousItem,
    currentItemSubIndex,
    isVideoStep,
    currentItem,
    handleNextClick,
    handlePreviousClick,
  };
}
